<!--
@Author: Kleber Pinel <kleberdasilva>
@Date:   2019-03-19T14:06:36-07:00
@Email:  kleber@hackcapital.com
@Last modified by:   kleberdasilva
@Last modified time: 2019-03-25T14:51:07-07:00
-->

<template lang="pug">
  section(v-if="!disabled")
    div(v-if="hasLDContentSlot")
      slot(name="ld-content" :visible="contentPickerVisible")
    content-picker(
      v-else
      :visible="contentPickerVisible"
      @close="resetToolbarElements"
      @insert="$emit('insert-ld-content', $event)"
    )
    div(v-if="hasURLImageSlot")
      slot(name="url-image" :visible="insertImageVisible")
    image-properties(
      v-else
      :visible="insertImageVisible"
      :value="imageMarkup"
      @close="resetToolbarElements"
      @insert="$emit('insert-url-image', $event)"
      title="Insert an image"
    )
    div(v-if="hasLinkURLSlot")
      slot(name="link-url" :visible="insertLinkVisible")
    insert-url(
      v-else
      :visible="insertLinkVisible"
      @close="resetToolbarElements"
      @insert="insertLinkUrl"
      title="Insert Link URL"
    )
    div(v-if="hasEditHTMLSlot")
      slot(name="edit-html" :visible="editHTMLVisible")
    edit-html(
      v-else
      :visible="editHTMLVisible"
      @close="resetToolbarElements"
      @insert="$emit('edit-html', $event)"
      :originalSource="textValue"
    )

    v-toolbar.editor-tools(dense flat)
      div(v-for="tool in enabledTools" :key="tool.name")
        emoji-picker(
          @emoji="$emit('insert-emoji', $event)"
          v-if="tool.name === 'Emoji'"
        )
          v-tooltip(bottom)
            span {{ tool.hint }}
            v-btn(flat slot="activator")
              v-icon {{ tool.icon }}
        v-tooltip(
          v-else-if="tool.name === 'Font'"
          bottom
        )
          span {{ tool.hint }}
          v-select.font-select(
            v-model="selectedFont"
            :items="fontNames"
            flat
            solo
            slot="activator"
            background-color="transparent"
          )
        v-tooltip(
          v-else-if="tool.name === 'Font Size'"
          bottom
        )
          span {{ tool.hint }}
          v-select.font-select(
            v-model="selectedFontSize"
            :items="fontSizes"
            flat
            solo
            slot="activator"
            background-color="transparent"
          )
        v-tooltip(
          v-else-if="tool.name === 'Font Color'"
          bottom
        )
          span {{ tool.hint }}
          editable-color-picker.color-picker(
            v-model="selectedFontColor"
            slot="activator"
            is-editing
          )
            v-btn(flat slot="trigger")
              v-icon {{ tool.icon }}
        v-tooltip(
          v-else-if="tool.name === 'Font Highlight'"
          bottom
        )
          span {{ tool.hint }}
          editable-color-picker.color-picker(
            v-model="selectedHighlightColor"
            slot="activator"
            is-editing
          )
            v-btn(flat slot="trigger")
              v-icon {{ tool.icon }}
        v-tooltip(bottom v-else)
          span {{ tool.hint }}
          v-btn(
            flat
            slot="activator"
            :data-cy="`rich-text-${tool.command}`"
            :class="{ active: tool.isActive }"
            @click='onCommand(tool.command, tool.commandArgument, tool.customCommand)'
          )
            v-icon(v-if="tool.icon")  {{ tool.icon }}
            span(v-else)  {{ tool.label }}
        .divider(v-if="tool.isSectionEnd")
      slot(name="custom")
</template>

<script>
import { mapGetters } from 'vuex';
import ContentPicker from './ContentPicker';
import EditHtml from './EditHtml';
import EmojiPicker from './EmojiPicker';
import ImageProperties from './ImageProperties';
import InsertUrl from './InsertUrl';
import EditableColorPicker from '@/AccountManagement/components/EditableColorPicker';
import { fontNames, fontSizes } from '../../config/fonts-toolbar-options';

export default {
  components: {
    ContentPicker,
    InsertUrl,
    ImageProperties,
    EditHtml,
    EmojiPicker,
    EditableColorPicker,
  },
  mounted () {
    this.validateSlots();
  },
  update() {
    this.validateSlots();
  },
  props: {
    attachmentHandlerId: {
      type: String,
      default: '#',
    },
    textValue: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    toggleToolbarItemVisible: {
      type: Boolean,
      default: true,
    },
    tools: {
      type: Array,
      default: [],
    },
    imageSelection: {
      type: String,
      default: null,
    },
    font: {
      type: String,
      default: fontNames[7].value,
    },
    fontSize: {
      type: String,
      default: '14px',
    },
    fontColor: {
      type: String,
      default: '#000',
    },
    highlightColor: {
      type: String,
      default: '#000',
    },
  },
  data() {
    return {
      linkText: '',
      imageURL: '',
      imageMarkup: '',
      sourceEditing: this.textValue,
      showSource: false,
      contentPickerVisible: false,
      insertImageVisible: false,
      insertLinkVisible: false,
      editHTMLVisible: false,
      emojiPickerVisible: false,
      hasLDContentSlot: false,
      hasURLImageSlot: false,
      hasLinkURLSlot: false,
      hasEditHTMLSlot: false,
      fontNames,
      fontSizes,
    };
  },
  watch: {
    // If this values change, we need to close the opened component
    toggleToolbarItemVisible(newValue) {
      this.resetToolbarElements();
    },
    imageSelection(newValue) {
      this.imageMarkup = newValue;
    },
  },
  computed: {
    ...mapGetters('userInformation', ['userHasFeatureFlag']),
    enabledTools() {
      return this.tools.filter(t => !t.featureFlag || this.userHasFeatureFlag(t.featureFlag));
    },
    selectedFont: {
      get() {
        const registeredFont = fontNames.map(f => f.value).includes(this.font);
        return registeredFont ? this.font : fontNames[7].value;
      },
      set(value) {
        this.$emit('change-font', value);
      },
    },
    selectedFontSize: {
      get() {
        return this.fontSize || '14px';
      },
      set(value) {
        this.$emit('change-font-size', value);
      },
    },
    selectedFontColor: {
      get() {
        return this.fontColor;
      },
      set(value) {
        this.$emit('change-font-color', value);
      },
    },
    selectedHighlightColor: {
      get() {
        return this.highlightColor;
      },
      set(value) {
        this.$emit('change-highlight-color', value);
      },
    },
  },
  methods: {
    notNullOrEmptySlot(scopedSlot) {
        const noEmptyNode = (hasContent, node) => {
          if (node instanceof Array) {
            return this.notNullOrEmptySlot(node);
          }
          return hasContent || (!!node && !!node.tag)
        };
        if (!scopedSlot) {
          return false;
        }

        if (typeof scopedSlot === 'function') {
          const slotFn = scopedSlot;
          const slotNode = slotFn();
          return this.notNullOrEmptySlot(slotNode);
        }

        if (scopedSlot.tag) {
          return true;
        }
        
        
        const result = !!scopedSlot && scopedSlot.reduce(noEmptyNode, false);
        return result;
    },
    validateSlots() {
      this.hasLDContentSlot = this.notNullOrEmptySlot(this.$scopedSlots['ld-content']);
      this.hasURLImageSlot = this.notNullOrEmptySlot(this.$scopedSlots['url-image']);
      this.hasLinkURLSlot = this.notNullOrEmptySlot(this.$scopedSlots['link-url']);
      this.hasEditHTMLSlot = this.notNullOrEmptySlot(this.$scopedSlots['edit-html']);
    },
    onCommand(command, textValue, customCommand) {
      switch (command) {
        case 'source':
          this.editHTMLVisible = !this.editHTMLVisible;
          return;
        case 'ImageUrl':
          this.insertImageVisible = !this.insertImageVisible;
          return;
        case 'ldContent':
          this.contentPickerVisible = !this.contentPickerVisible;
          return;
        case 'emoji':
          this.emojiPickerVisible = !this.emojiPickerVisible;
          return;
        case 'createLink': {
          const selRange = window.getSelection();
          const selText = selRange.toString();
          this.linkText = selText;
          this.insertLinkVisible = !this.insertLinkVisible;
          return;
        }
        case 'attachment':
          // TODO AK: refactor this using component $refs once to allow
          // for multiple instances of AttachmentHandler and better component separation
          document.querySelector(`#${this.attachmentHandlerId}`).click();
          return;
        case 'ldVideo':
          this.$emit('handle-video-library');
          return;
        default:
          this.$emit('exec-command', command, textValue);
      }
    },

    insertLinkUrl(linkValue) {
      this.$emit('insert-link-url', this.linkText, linkValue);
    },

    resetToolbarElements() {
      this.linkText = '';
      this.imageURL = '';
      this.contentPickerVisible = false;
      this.insertImageVisible = false;
      this.insertLinkVisible = false;
      this.editHTMLVisible = false;
      this.emojiPickerVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-tools /deep/ .v-toolbar__content {
  padding: 4px;
  flex-wrap: wrap;
  height: initial !important;
  white-space: normal;
}
.color-picker {
  display: inline-block;
}
.editor-tools {
  .v-btn, .v-select {
    padding: 0px 4px;
    height: 30px;
    min-width: 0px;
    margin: 2px;
    opacity: 0.5;
    border-radius: 2px;
    text-transform: initial;
    &.active {
      opacity: 1;
      &:before {
        background-color: currentColor;
      }
    }

    &.font-select {
      font-weight: bold;
      display: inline-block;
      
      &:hover {
        background-color: #dbdbdb;
      }
      
      & /deep/ .v-input__control {
        min-height: 0px;

        .v-input__slot {
          margin: 0;
          padding: 0px 5px;
          border-radius: 2px;

          .v-select__selections {
            .v-select__selection.v-select__selection--comma {
              margin: 7px 0px;
              max-width: 100%;
            }

            input {
              display: none;
            }
          }

          .v-input__append-inner {
            display: none;
          }
        }
      }

    }
  }
  .divider {
    height: 20px;
    width: 1px;
    border-left: solid 1px #ccc;
    margin: 0 2px;
    display: inline;
  }
}
</style>
